import {IconButton, MenuItem, Paper} from '@material-ui/core';
import {Close} from '@material-ui/icons';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import {Scope} from 'Shared/constants/Scope';
import {hasAccessToScopeByApplication} from 'Shared/utilities/applicationId';
import AgentPortalEntry from 'Users/components/UserAdministration/UserEntry/Agent/AgentPortalEntry';
import AgentSupportUserTypeEntry from 'Users/components/UserAdministration/UserEntry/Agent/AgentSupportUserTypeEntry';
import VIPUserTypeEntry from 'Users/components/UserAdministration/UserEntry/Agent/VipUserTypeEntry';
import {changeAgentRole, removeUserType} from 'Users/ducks/userEntry';
import {Application} from 'Users/models/Application';
import {AgentRole, InvalidAgentUserType, matchAgentUserType, ValidAgentUserType} from 'Users/models/User/AgentUserType';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './AgentUserTypeEntry.sass';
import { User } from 'Users/models/User/User';
import { isQuotingAdminUserType } from 'Users/models/User/QuotingUserType';
import { UserType } from 'Users/models/User/UserType';
import * as I from 'immutable';
import { ReducerState } from 'rootReducer';


type AgentUserTypeEntryProps = {
  agentUserType: InvalidAgentUserType | ValidAgentUserType;
  isEdit: boolean;
  isExternal: boolean;
};

const AgentUserTypeEntry = (props: AgentUserTypeEntryProps) => {
  const dispatch = useDispatch();
  const {agentUserType, isExternal} = props;
  const {agentRole} = agentUserType.value;

  const scopes = useSelector(selectUserScopes);
  const hasUpdateAccess = hasAccessToScopeByApplication(scopes, Scope.UpdateUser, Application.Agent);
  const user = useSelector((state: ReducerState) => state.user.userEntry);

  const userTypes: I.List<UserType> = user.userTypes;
  const userTypeValues = userTypes.map(ut => ut.value); 
  
  return (
    <Paper className="agent-user-type-entry">
      <IconButton
        className="agent-user-type-entry__remove-button"
        onClick={() => dispatch(removeUserType(agentUserType))}
        disabled={!hasUpdateAccess && props.isEdit}
      >
        <Close />
      </IconButton>
      <p className="agent-user-type-entry__title">Add Agent Permissions</p>
      <p className="agent-user-type-entry__description">
        Select a role and complete the appropriate fields to add agent permissions to this user.
      </p>
      <OutlinedSelect
        className="agent-user-type-entry__role-select"
        label="Agent Role"
        value={agentRole || ''}
        onChange={e => {
          dispatch(changeAgentRole(parseInt(e.target.value, 10) as AgentRole));
        }}
        disabled={!hasUpdateAccess && props.isEdit}
      >
        <MenuItem value={AgentRole.VIP} disabled={isExternal}>
          VIP
        </MenuItem>
        <MenuItem value={AgentRole.IAM} disabled={isExternal || userTypeValues.some(isQuotingAdminUserType)}>
          IAM
        </MenuItem>
        <MenuItem value={AgentRole.Agent} disabled={userTypeValues.some(isQuotingAdminUserType)}>Agent</MenuItem>
        <MenuItem value={AgentRole.AgentSupport} disabled={userTypeValues.some(isQuotingAdminUserType)}>Agent Support</MenuItem>
      </OutlinedSelect>
      {matchAgentUserType(agentUserType.value, {
        Undetermined: (): React.ReactNode => null,
        VIP: vipUserType => <VIPUserTypeEntry vipUserType={vipUserType} />,
        IAM: (): React.ReactNode => null,
        Agent: agentUserType => (
          <AgentPortalEntry agentUserType={agentUserType} isEdit={props.isEdit} hasUpdateAccess={hasUpdateAccess} />
        ),
        AgentSupport: agentSupportUserType => (
          <AgentSupportUserTypeEntry
            agentSupportUserType={agentSupportUserType}
            isEdit={props.isEdit}
            hasUpdateAccess={hasUpdateAccess}
          />
        ),
      })}
    </Paper>
  );
};

export default AgentUserTypeEntry;
